import { motion, useReducedMotion } from 'motion/react';
import { memo } from 'react';

import { Icons } from '@/assets/icons/icons';
import { SlotMachineText } from '@/components/blocks/Message/turn-activity/SlotMachineText';
import { XStack } from '@/components/ui/layout/layout';
import { ARTIFACT_CARD_HEIGHT_PX } from '@/features/artifacts/artifact.constants';

import { ArtifactCodePreview } from './ArtifactCodePreview';
import { ArtifactIconCycle } from './ArtifactIconCycle';
import { useCycledIndex } from './useCycledIndex';

const GENERATING_PHRASES = [
  'Sketching the layout…',
  'Laying the foundations…',
  'Arranging the pieces…',
  'Shaping the structure…',
  'Polishing details…',
];
const PHRASE_DWELL_MS = 2_800;

export const ArtifactGeneratingCard = memo(function ArtifactGeneratingCard() {
  const shouldReduceMotion = useReducedMotion() ?? false;
  const phraseIndex = useCycledIndex(
    GENERATING_PHRASES.length,
    PHRASE_DWELL_MS,
    !shouldReduceMotion
  );
  const phrase = GENERATING_PHRASES[phraseIndex];

  return (
    <motion.div
      initial={shouldReduceMotion ? false : { opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: shouldReduceMotion ? 0 : 0.2, ease: 'easeOut' }}
      style={{ height: ARTIFACT_CARD_HEIGHT_PX + 8 }}
      className="overflow-hidden"
    >
      <XStack
        center
        data-testid="artifact-generating-card"
        style={{ height: ARTIFACT_CARD_HEIGHT_PX }}
        className="mt-2 w-full min-w-0 gap-3 overflow-hidden rounded-2xl border border-grayA-3 bg-surface px-4 group-data-[compact=true]/reasoning:bg-grayA-2"
      >
        <div className="shrink-0 text-foreground-muted">
          {shouldReduceMotion ? (
            <Icons.filePlus className="size-4" />
          ) : (
            <ArtifactIconCycle size={16} />
          )}
        </div>
        <div className="min-w-0 flex-1">
          {shouldReduceMotion ? (
            <span className="block truncate text-sm font-medium leading-5 text-[var(--reasoning-accent)]">
              {phrase}
            </span>
          ) : (
            <SlotMachineText
              text={phrase}
              textClassName="text-sm font-medium leading-5"
            />
          )}
        </div>
        <div aria-hidden="true" className="h-full w-1/2 shrink-0">
          <ArtifactCodePreview animate={!shouldReduceMotion} />
        </div>
      </XStack>
    </motion.div>
  );
});
